import { useSelector } from 'react-redux';
import {
    Button,
    TextField,
    FormControlLabel,
    RadioGroup,
    Radio,
    Chip,
    CircularProgress
} from '@mui/material';
import { BorderColor, Description, Save } from '@mui/icons-material';
import useInvoiceInfo from '../hooks/useInvoiceInfo';
import { H4, H6, Body2, Body3 } from '../../../../../themes/typography';

function InvoiceInfo() {
    const { formik, loading } = useInvoiceInfo();
    const user = useSelector((state) => state.auth.user);

    return (
        <div className="invoice-info-main-container">
            <form id="personal-info-form" onSubmit={formik.handleSubmit} noValidate>
                <div className="invoice-info-header">
                    <div className="invoice-info-title">
                        <span className="invoice-info-icon">
                            <Description color="secondary" sx={{ fontSize: '1.75rem' }} />
                        </span>
                        <H4 fontWeight={600}>Invoice Information</H4>
                    </div>

                    {!user?.verification?.pan_verified && (
                        <div className="invoice-info-edit-button">
                            <Button
                                variant="text"
                                type="button"
                                startIcon={
                                    loading ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : formik.values.isEditable ? (
                                        <Save />
                                    ) : (
                                        <BorderColor />
                                    )
                                }
                                disableRipple
                                onClick={() => {
                                    if (formik.values.isEditable) {
                                        formik.handleSubmit();
                                    } else {
                                        formik.setFieldValue('isEditable', true);
                                    }
                                }}
                                disabled={loading}
                            >
                                {formik.values.isEditable ? (loading ? 'Saving' : 'Save') : 'Edit'}
                            </Button>
                        </div>
                    )}
                </div>
                <div className="invoice-info-body">
                    <div className="invoice-info-details-preview">
                        <div className="invoice-info-preview-item">
                            <div className="invoice-info-preview-label">
                                <Body3 fontWeight={300}>Pan Number</Body3>
                            </div>
                            <div className="invoice-info-preview-value" style={{ gap: '0.75rem' }}>
                                <Body2 fontWeight={600}>{user?.verification?.pan_number || 'Not Provided'}</Body2>

                                {/* {!user?.verification?.pan_verified && (
                                    <Chip label="In review" color="warning" size="small" />
                                )}

                                {user?.verification?.pan_verified && (
                                    <Chip label="Verified" color="success" size="small" />
                                )} */}
                            </div>
                        </div>

                        {(user?.verification?.gst_number === null || user?.verification?.gst_number === '') && (
                            <div className="invoice-info-preview-item">
                                <div className="invoice-info-preview-label">
                                    <Body3 fontWeight={300}>GST Status</Body3>
                                </div>
                                <div className="invoice-info-preview-value">
                                    <Body2 fontWeight={600}>Not Provided</Body2>
                                </div>
                            </div>
                        )}

                        {user?.verification?.gst_number && (
                            <div className="invoice-info-preview-item">
                                <div className="invoice-info-preview-label">
                                    <Body3 fontWeight={300}>GST Number</Body3>
                                </div>
                                <div className="invoice-info-preview-value" style={{ gap: '0.75rem' }}>
                                    <Body2 fontWeight={600}>
                                        {user?.verification?.gst_number}
                                    </Body2>

                                    {!user?.verification?.gst_verified && (
                                        <Chip label="In review" color="warning" size="small" />
                                    )}

                                    {user?.verification?.gst_verified && (
                                        <Chip label="Verified" color="success" size="small" />
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="invoice-info-details">
                        <div className="invoice-info-two-inputs">
                            <div className="invoice-info-input-field">
                                <TextField
                                    required
                                    fullWidth
                                    id="panNumber"
                                    label="PAN Number"
                                    name="panNumber"
                                    value={formik.values.panNumber}
                                    disabled={!formik.values.isEditable}
                                    onChange={(e) =>
                                        formik.setFieldValue(
                                            'panNumber',
                                            e.target.value.toUpperCase()
                                        )
                                    }
                                    error={
                                        formik.touched.panNumber && Boolean(formik.errors.panNumber)
                                    }
                                    helperText={formik.touched.panNumber && formik.errors.panNumber}
                                    slotProps={{ htmlInput: { maxLength: 10 } }}
                                />
                            </div>
                            <div className="invoice-info-input-field gst-radio-btns">
                                <H6>GST Registration</H6>
                                <RadioGroup
                                    row
                                    aria-labelledby="gst-registration-label"
                                    name="gst-registration"
                                    sx={{ gap: '1.5rem' }}
                                    value={formik.values.isGstRegistered}
                                    onChange={(e) =>
                                        formik.setFieldValue(
                                            'isGstRegistered',
                                            e.target.value === 'true'
                                        )
                                    }
                                >
                                    <FormControlLabel
                                        label="Yes"
                                        value={true}
                                        sx={{ gap: '0.75rem' }}
                                        control={
                                            <Radio
                                                sx={{ padding: '0' }}
                                                disabled={!formik.values.isEditable}
                                            />
                                        }
                                    />
                                    <FormControlLabel
                                        label="No"
                                        value={false}
                                        sx={{ gap: '0.75rem' }}
                                        control={
                                            <Radio
                                                sx={{ padding: '0' }}
                                                disabled={!formik.values.isEditable}
                                            />
                                        }
                                    />
                                </RadioGroup>
                            </div>
                        </div>
                        {formik.values.isGstRegistered && (
                            <div className="invoice-info-two-inputs">
                                <div className="invoice-info-input-field">
                                    <TextField
                                        fullWidth
                                        id="gstNumber"
                                        label="GST Number"
                                        name="gstNumber"
                                        value={formik.values.gstNumber}
                                        disabled={!formik.values.isEditable}
                                        onChange={(e) =>
                                            formik.setFieldValue(
                                                'gstNumber',
                                                e.target.value.toUpperCase()
                                            )
                                        }
                                        error={
                                            formik.touched.gstNumber &&
                                            Boolean(formik.errors.gstNumber)
                                        }
                                        helperText={
                                            formik.touched.gstNumber && formik.errors.gstNumber
                                        }
                                        slotProps={{ htmlInput: { maxLength: 15 } }}
                                    />
                                </div>
                                <div className="invoice-info-input-field"></div>
                            </div>
                        )}
                    </div>
                </div>
            </form>
        </div>
    );
}

export default InvoiceInfo;